/**
 * A `sessionStorage`-guarded, at-most-once-per-cooldown hard reload.
 *
 * Extracted from `lib/chunks/chunkErrors.ts` (TRA-342/TRA-346) so that any
 * "this document is wedged and only a fresh navigation can fix it" recovery shares
 * one loop-proof guard instead of each growing its own. Callers own the decision of
 * *when* a reload is warranted and pass a distinct `markerKey`, so an unrelated
 * recovery (a stale chunk vs. a dead Turnstile widget) never spends the other's
 * cooldown.
 *
 * The marker is a timestamp, not a boolean: a tab that recovers cleanly once
 * shouldn't be permanently unguarded for the rest of its life — a long-lived tab
 * can hit two genuinely separate incidents hours apart — while the cooldown floor
 * between reloads still rules out a loop.
 *
 * If `sessionStorage` can't be read or written at all (private-mode lockdown, a
 * third-party-frame context, quota exhaustion), we can't durably guarantee the
 * cooldown, so we deliberately do NOT reload rather than risk reloading on every
 * mount — a loop is a strictly worse outcome than a user occasionally missing an
 * automatic recovery.
 */

export const DEFAULT_RELOAD_COOLDOWN_MS = 10 * 60 * 1000;

// Give any telemetry queued just before the reload a moment to flush its batch before
// navigation — a synchronous reload right after queuing an event is a known way to
// lose it.
export const DEFAULT_RELOAD_DELAY_MS = 200;

export type ReloadOnceOptions = {
  /** `sessionStorage` key holding the timestamp of the last reload this caller issued. */
  markerKey: string;
  cooldownMs?: number;
  delayMs?: number;
  /**
   * Fired synchronously once the guard has passed and the marker is written, before
   * the reload timer is armed. The only place to capture telemetry that must describe
   * a reload that is really going to happen (never a suppressed attempt), and that
   * still gets the delay below to flush.
   */
  onBeforeReload?: () => void;
};

/**
 * Hard-reloads the page after `delayMs`, unless one was already issued under
 * `markerKey` within `cooldownMs` or `sessionStorage` is unusable.
 *
 * Returns `true` if a reload was scheduled (useful for tests and for callers that
 * report whether recovery was attempted; the reload navigates the page away once it
 * fires, so there is nothing else to do either way).
 */
export function reloadOnce({
  markerKey,
  cooldownMs = DEFAULT_RELOAD_COOLDOWN_MS,
  delayMs = DEFAULT_RELOAD_DELAY_MS,
  onBeforeReload,
}: ReloadOnceOptions): boolean {
  if (typeof window === "undefined") return false;

  const now = Date.now();
  try {
    const last = window.sessionStorage.getItem(markerKey);
    if (last !== null && now - Number(last) < cooldownMs) return false;
    window.sessionStorage.setItem(markerKey, String(now));
  } catch {
    return false;
  }

  onBeforeReload?.();
  setTimeout(() => window.location.reload(), delayMs);
  return true;
}
